import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginScreen from './components/pages/LoginScreen';
import Dashboard from './components/pages/Dashboard'; 
import Profile from './components/pages/Profile'; 
import Membership from './components/pages/Membership';
import Contact from './components/pages/Contact';
import PrivateRoute from './components/config/PrivateRoute';
import Settings from './components/pages/Settings';
import ForgotPassword from './components/pages/ForgotPassword';
import AddService from './components/pages/AddService';
import Manage from './components/pages/Manage';
import Notifications from './components/pages/Notifications';
import NotificationListener from './components/common/NotificationListener';
import Toast from './components/common/Toast';
import { requestNotificationPermission, auth } from './components/config/firebase';
import { SpeedInsights } from "@vercel/speed-insights/react";
import {
  registerFCMToken,
  getServiceProviderAverageRating,
  getMembershipStatus,
  cacheMembershipStatus,
} from "./services/coreService";
import { getFirebaseToken } from './utils/auth';
import AddEventService from './components/pages/AddListingService';
import ManageEventServices from './components/pages/ManageListingServices';
import EditEventService from './components/pages/EditListingService';
import EventServiceDetails from './components/pages/EventServiceDetails';
import MarkBusy from './components/pages/MarkBusy';
import ChangeMobile from './components/pages/ChangeMobile';
import './i18n';
import BookingCalendar from './components/pages/BookingCalendar';
import Leads from './components/pages/Leads';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import TermsAndConditions from './components/pages/TermsAndConditions';

function App() {
  const [toast, setToast] = useState(null);

  // Monitor Firebase auth state and initialize token refresh
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        console.log('User authenticated, initializing token refresh...');
        // Trigger getFirebaseToken which will setup auto-refresh
        await getFirebaseToken();
      }
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    async function setupFCM() {
      // Only register FCM token if user is authenticated with Firebase
      // This prevents 401 errors when backend requires Firebase auth token
      const firebaseUser = auth.currentUser;
      const localUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (firebaseUser && localUser.spId) {
        const token = await requestNotificationPermission();
        if (token) {
          console.log('App.js - Registering FCM token for authenticated user');
          await registerFCMToken(localUser.spId, token);
        }
      } else {
        console.log('App.js - Skipping FCM token registration (user not authenticated with Firebase)');
      }
    }
    setupFCM();
  }, []);

  useEffect(() => {
    async function prefetchProviderRating(spId) {
      if (!spId) return;

      const prefetchedKey = `providerAverageRatingPrefetched:${spId}`;
      if (sessionStorage.getItem(prefetchedKey) === 'true') return;

      const ratingRes = await getServiceProviderAverageRating(spId);
      const rating = Number(ratingRes?.responseData);
      if (Number.isFinite(rating)) {
        localStorage.setItem(`providerAverageRating:${spId}`, String(rating));
      }
      sessionStorage.setItem(prefetchedKey, 'true');
    }

    async function prefetchMembershipStatus(spId) {
      if (!spId) return;

      const prefetchedKey = `membershipStatusPrefetched:${spId}`;
      if (sessionStorage.getItem(prefetchedKey) === 'true') return;

      const membershipRes = await getMembershipStatus(spId);
      if (membershipRes) {
        cacheMembershipStatus(spId, membershipRes);
      }
      sessionStorage.setItem(prefetchedKey, 'true');
    }

    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    const spId = localUser.spId || localUser.userId;
    if (!spId) return;

    // Ensure Firebase auth is available before requesting protected rating API.
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser) return;
      await Promise.all([
        prefetchProviderRating(spId),
        prefetchMembershipStatus(spId),
      ]);
    });

    return () => unsubscribe();
  }, []);

  return (
    <Router>
      <NotificationListener
        onNewNotification={notif => {
          setToast({
            message: notif.title || notif.body || notif.message || "New notification",
          });
        }}
      />
      {toast && (
        <Toast
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/register" element={<LoginScreen />} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/contact" element={<PrivateRoute><Contact /></PrivateRoute>} />
        <Route path="/membership" element={<PrivateRoute><Membership /></PrivateRoute>} />
        <Route path="/add-service" element={<PrivateRoute><AddService /></PrivateRoute>} />
        <Route path="/manage" element={<PrivateRoute><Manage /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
        <Route path="/add-event-service" element={<PrivateRoute><AddEventService /></PrivateRoute>} />
        <Route path="/manage-event-services" element={<PrivateRoute><ManageEventServices /></PrivateRoute>} />
        <Route path="/edit-event-service/:esId" element={<PrivateRoute><EditEventService /></PrivateRoute>} />
        <Route path="/event-service/:esId" element={<PrivateRoute><EventServiceDetails /></PrivateRoute>} />
        <Route path="/mark-busy" element={<PrivateRoute><MarkBusy /></PrivateRoute>} />
        <Route path="/booking-calendar" element={<PrivateRoute><BookingCalendar /></PrivateRoute>} />
        <Route path="/change-mobile" element={<PrivateRoute><ChangeMobile /></PrivateRoute>} />
        <Route path="/leads" element={<PrivateRoute><Leads /></PrivateRoute>} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      <SpeedInsights />
    </Router>
  );
}

export default App;
